import { createMultifileUploadSlice, speedStream, stagedFiles } from "@nova/slice-multifile-upload"
import { withRoot } from "@nova/with-root"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import {
    AnyAction,
    BoundingBox,
    hasLRFTags,
    HotspotStatus,
    ID,
    ImageProcessingState,
    ImageType,
    ImageWarnings,
    isUsable,
    PreviouslyParsedTagValues,
    SidePanelLayerType,
} from "@nova/constants"
import { get, last, memoize } from "nodash"
import {
    selectFlightOrthoBoundingBox,
    selectFlightsWithRemovedHotspots,
    selectInfraredFlightById,
    selectRemovedHotspotsIsVisible,
    selectVisibleInfraredFlights,
} from "features/entity-caching/flights/selectors"
import { createInfraredImage } from "features/pubsub/entityActions/images/infrared"
import { InfraredImage } from "@nova/protobuf"
import { dateAsNumber } from "@nova/serializer/src/client/util"
import {
    calculateBoundingBoxCornerPoints,
    combineBoundingBoxes,
    GeospatialPoint,
} from "@nova/calculate-geospatial"
import { BoundingBoxLayerData } from "plugins/shared/layers/BoundingBoxLayer.class"
import { selectInfraredImagesAndUploadsByFlightId } from "features/entity-caching/images/hotspots/selectors"
import store from "integrations/redux/store"
import { selectHotspotsByFlightId } from "features/entity-caching/hotspots/selectors"
import { selectOrthoOverlapBoundingBoxMap } from "plugins/Orthomosaic/OrthomosaicSlice"
import { createIdFromFile, getEnumValue } from "@nova/util-strings"
import { addFlightUsage } from "features/data-management/workspace/subscriptions/module"
import {
    selectLastSelectedLayerId,
    selectSelectedLayers,
} from "panels/content/side-panel/slice.new"
import {
    selectMergedFlightById,
    selectMergedFlights,
} from "features/entity-caching/mergedFlights/selectors"
import { updateEntitySocket } from "features/entity-caching/project/slice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { getNumberOfWorkers, runTask } from "@nova/webworker-pool"
import { convertImageToTiff } from "integrations/sensortool/convertImageToTiff"
import { processImage } from "integrations/sensortool/processImage"
import {
    convertImageBufferWithCsv,
    csvConversionKind,
    drainPendingCsvDrop,
    getFlightCsv,
    makeAttachCsvFiles,
} from "features/job-management/uploader/mma-telemetry"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { getAncestors } from "panels/content/side-panel/utils"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { IngestDataType } from "@nova/schemas"
import { createSpeedCallback } from "themes/default-tailwind/panels/GlobalUploadContainer"
import { AutoProcessController } from "panels/content/side-panel/upload-flow/preview-files/AutoProcessController"

async function compressGzip(file: ArrayBuffer | Uint8Array) {
    const blob = new Blob([file as BlobPart])
    const ds = new CompressionStream("gzip")

    const compressedStream = blob.stream().pipeThrough(ds)

    const response = new Response(compressedStream)
    const buffer = await response.arrayBuffer()

    return new Uint8Array(buffer)
}

type InfraredImageMeta = {
    organizationID: string
    fireID: string
    flightID: string
    setID: string
    file: File
    options: { useTiff?: boolean; rapidMode?: boolean }
}

export type InfraredImageState = Partial<InfraredImage> & {
    state: ImageProcessingState
    id: string
    warnings?: ImageWarnings
    deletedAt?: AnyAction
    tags?: Partial<PreviouslyParsedTagValues>
    hasLRF?: boolean
    projectedBbox?: number[][]
    fileName?: string
    hasThermalData?: boolean
    flightId?: ID
    // Staged via LoadFromLayer's stageRemote (no backing File) — watched by
    // useImportArrivalWatcher to flip to IngestDone once the real entity lands.
    isRemoteImport?: boolean
}

const createIRIdFromFile = (file: File, forceTiff = false) => {
    if (forceTiff)
        return createIdFromFile({
            name: file.name.split(".").slice(0, -1).join(".") + ".tiff",
            size: file.size,
            lastModified: file.lastModified,
        })
    return createIdFromFile(file)
}

const {
    actions,
    reducer: imagesReducer,
    selectors: {
        selectAllUploads,
        selectAllSets,
        selectSet,
        selectUpload,
        selectUploadsForSet,
        selectUploadIdsForSet,
        selectCancelledUploadIdsForSet,
        selectUploadsForSetSummary: baseSelectUploadsForSetSummary,
        selectPendingForSet: baseSelectPendingForSet,
    },
} = createMultifileUploadSlice<"infrared-images-v1", InfraredImageState, InfraredImageMeta>(
    "infrared-images-v1",
    (m: InfraredImageMeta) => {
        // .png/.owi files are converted to TIFF before upload. Give them a .tiff
        // ID so the S3 key and entity record use the correct extension from the start.
        const rawId = createIdFromFile(m.file)
        const id = csvConversionKind(m.file.name) ? rawId.replace(/\.(png|owi)$/i, ".tiff") : rawId
        return {
            id,
            state: ImageProcessingState.Pending,
            fileName: m.file.name,
        } satisfies InfraredImageState
    },
    {
        addUploadToProject: (setId, upload) => {
            delete upload.tags
            return createInfraredImage({
                data: upload as InfraredImage,
                context: { flightId: setId },
            })
        },
    },
    async (state, meta, update, { useSignal, useSemaphore }) => {
        const uploadSignal = useSignal("upload", meta.setID)
        const deleteSignal = useSignal("delete", state.id)
        deleteSignal.then(() => {
            if (
                state.state !== ImageProcessingState.UploadDone &&
                state.state !== ImageProcessingState.IngestDone
            )
                state = { ...state, deletedAt: new Date().toISOString() }
        })

        const xmlFile = get(meta.file, "xml") as File | undefined
        // .png (16-bit thermal) and .owi (NIR) are converted to TIFF via MMA
        // telemetry before upload; everything else is uploaded as-is.
        const conversionKind = csvConversionKind(meta.file.name)

        // Converted image buffer — set during processing for converted files, used during upload.
        let tiffBuffer: ArrayBuffer | undefined
        let hadInvalidTelemetry = false
        await useSemaphore(getNumberOfWorkers() * 3, async () => {
            update({ ...state, state: ImageProcessingState.Processing })
            if (state.deletedAt) return
            const [fileBuffer, xmlFileBuffer] = await Promise.all([
                meta.file.arrayBuffer(),
                xmlFile?.arrayBuffer(),
            ])

            if (conversionKind) {
                // Wait for attachCsvFiles to populate the registry and fire the csv
                // signal. If telemetry is already present (CSV dropped first, or
                // another file in this batch triggered it), skip the wait.
                if (!getFlightCsv(meta.setID)) {
                    await useSignal("csv", meta.setID)
                }

                if (state.deletedAt) return

                const result = await convertImageBufferWithCsv(
                    meta.file.name,
                    fileBuffer,
                    meta.setID
                )

                if (result.status !== "ok" || state.deletedAt) {
                    if (!state.deletedAt) {
                        hadInvalidTelemetry = true
                        update({
                            ...state,
                            error: { name: "InvalidTelemetry", message: "No matching CSV row" },
                        } as typeof state)
                    }
                    return
                }

                tiffBuffer = result.buffer

                // Run the generated TIFF through sensortool so metadata extraction
                // uses the same canonical path as every other image type.
                // No transfer list — tiffBuffer must remain intact for the upload step.
                const metadata = await runTask(processImage, {
                    fileBuffer: tiffBuffer,
                    xmlFileBuffer: undefined,
                })

                if (state.deletedAt) return

                state = {
                    ...state,
                    ...metadata?.gps,
                    flightId: meta.setID,
                    takenAt: dateAsNumber(metadata?.takenAt),
                    state: ImageProcessingState.ProcessingDone,
                    tags: metadata?.tags as PreviouslyParsedTagValues,
                    hasLRF: hasLRFTags(metadata?.tags as PreviouslyParsedTagValues),
                    pitch: metadata?.pyr?.pitch || -90,
                    yaw: metadata?.pyr?.yaw || 0,
                    roll: metadata?.pyr?.roll || 0,
                    height: metadata?.height || 0,
                    fov: Array.from(metadata?.fov || []),
                    make: metadata?.make,
                    model: metadata?.model,
                    focalLength: metadata?.focalLength,
                    size: meta.file.size,
                    imageHeight: metadata?.imageHeight,
                    imageWidth: metadata?.imageWidth,
                    imageType: conversionKind === "owi" ? ImageType.OWI : ImageType.PNG,
                    // .png is thermal (LWIR); .owi (NIR) is not. Trust sensortool's
                    // classification, defaulting only for the thermal PNG case.
                    hasThermalData: metadata?.hasThermalData ?? conversionKind === "png",
                    fileName: meta.file.name,
                }
            } else {
                const metadata = await runTask(
                    processImage,
                    { fileBuffer, xmlFileBuffer },
                    { transfer: [fileBuffer, xmlFileBuffer].filter((e) => !!e) }
                )

                if (state.deletedAt) return
                state = {
                    ...state,
                    ...metadata?.gps,
                    flightId: meta.setID,
                    takenAt: dateAsNumber(metadata?.takenAt),
                    state: ImageProcessingState.ProcessingDone,
                    tags: metadata?.tags as PreviouslyParsedTagValues,
                    hasLRF: hasLRFTags(metadata?.tags as PreviouslyParsedTagValues),
                    pitch: metadata?.pyr?.pitch || -90,
                    yaw: metadata?.pyr?.yaw || 0,
                    roll: metadata?.pyr?.roll || 0,
                    height: metadata?.height || 0,
                    fov: Array.from(metadata?.fov || []),
                    make: metadata?.make,
                    model: metadata?.model,
                    focalLength: metadata?.focalLength,
                    size: meta.file.size,
                    imageHeight: metadata?.imageHeight,
                    imageWidth: metadata?.imageWidth,
                    imageType: getEnumValue(ImageType, last(meta.file.name.split(".")) || ""),
                    hasThermalData: metadata?.hasThermalData,
                    fileName: meta.file.name,
                }
            }
            update(state)
        })

        if (hadInvalidTelemetry) return

        await uploadSignal

        let ingestPromise: Promise<unknown> | undefined
        let hadIngestError = false
        await useSemaphore(45, async () => {
            // cant hold all buffers for all files in memory at the same time, need to reread here in a semaphore
            if (xmlFile) {
                update({ ...state, state: ImageProcessingState.Uploading })
                delete state.tags
                await Promise.all([
                    fileManagementApi.putFile(meta.file, {
                        target: `public/org/${meta.organizationID}/fire/${meta.fireID}/flight/${meta.flightID}/infrared/original/${state.id}`,
                    }),
                    fileManagementApi.putFile(xmlFile, {
                        target: `public/org/${meta.organizationID}/fire/${meta.fireID}/flight/${meta.flightID}/infrared/original/${xmlFile.name}`,
                    }),
                ])
                update({ ...state, state: ImageProcessingState.IngestDone })
                store.dispatch(
                    createInfraredImage({
                        data: state as InfraredImage,
                        context: { flightId: state.flightId! },
                    })
                )
                return
            }
            const fileBuffer = await (!tiffBuffer
                ? meta.file.arrayBuffer()
                : Promise.resolve(tiffBuffer))

            let file: Uint8Array | ArrayBuffer = fileBuffer
            if (state.deletedAt) return
            state = {
                ...state,
                state: ImageProcessingState.Uploading,
            }

            let s3Done!: () => void
            const s3Signal = new Promise<void>((r) => (s3Done = r))
            const onS3Complete = () => {
                if (!state.deletedAt) {
                    update({ ...state, state: ImageProcessingState.UploadDone })
                }
                s3Done()
            }

            let compression = undefined as "gzip" | undefined
            if (meta.options.useTiff) {
                const previousId = state.id
                state.id = createIRIdFromFile(meta.file, true)
                // Re-key rather than drop it: a failed upload is only retryable while the picked
                // file is still reachable under the id the upload record now uses.
                stagedFiles[state.id] = meta.file
                delete stagedFiles[previousId]
                update({ ...state, previousId })
                const { tiff } = await runTask(
                    convertImageToTiff,
                    { fileBuffer },
                    { transfer: [fileBuffer] }
                )
                if (!tiff) throw new Error("Could not convert IRB to tiff")
                file = tiff
            } else {
                update({ ...state })
                file = await compressGzip(file)
                compression = "gzip"
            }
            ingestPromise = fileManagementApi.putFile(file, {
                filename: state.id,
                tags: {
                    dataType: IngestDataType.InfraredImage,
                    orgId: meta.organizationID,
                    projectId: meta.fireID,
                    flightId: meta.flightID,
                    ...(compression ? { compression: compression } : {}),
                },
                onProgress: createSpeedCallback(({ speedMbps }) => {
                    speedStream.setSpeed(meta.setID, state.id, speedMbps)
                }),
                onS3Complete,
                maxQueue: 15,
                onIngestError: (ingest) => {
                    hadIngestError = true
                    update({
                        ...state,
                        error: { name: "IngestError", message: `ingest ${ingest.status}` },
                    } as typeof state)
                },
            })

            await Promise.race([s3Signal, ingestPromise.catch(() => {})])
        })

        if (ingestPromise) {
            await ingestPromise
            if (!state.deletedAt && !hadIngestError) {
                update({ ...state, state: ImageProcessingState.IngestDone }, () => {
                    AutoProcessController.emit(`flightId:${meta.setID}:ingest-progress`)
                })
            }
        }

        return { ...state }
    },
    () => ({
        warnings: undefined,
    })
)

export const stageFiles = (
    organizationID: string,
    fireID: string,
    flightID: string,
    files: File[],
    options: { useTiff?: boolean },
    onFinish?: () => void
) => {
    // Drain any CSV-only drop that fileFilter set immediately before this call
    const csvFiles = drainPendingCsvDrop()
    if (csvFiles.length > 0) {
        store.dispatch(attachCsvFiles(flightID, csvFiles))
    }

    const meta = new Array<InfraredImageMeta>(files.length)
    let totalSize = 0
    for (let i = 0; i < files.length; i++) {
        meta[i] = {
            organizationID,
            fireID,
            flightID,
            setID: flightID,
            file: files[i],
            options,
        }
        totalSize += files[i].size
    }
    addFlightUsage(flightID, totalSize)
    return actions.stageFiles({ meta, onFinish })
}

export const uploadFlight = (id: string) => actions.signal("upload", id)
export const deleteUpload = actions.deleteUpload
export const deleteUploadSet = actions.deleteUploadSet
export const stageRemote = actions.stageRemote
export const updateManyUploads = actions.updateMany

// this is summarizing the state of uploads for a set
// it's useful for determining if a set is uploading, how much of it is uploading, etc.
const selectUploadsForSetSummary = (id: string) =>
    baseSelectUploadsForSetSummary(
        id,
        (t) => t.state,
        (t) => t.state === ImageProcessingState.IngestDone
    )

const selectPendingUploadsForSet = (id: string) => baseSelectPendingForSet(id, "upload")

export const selectInfraredJobByFlightId = memoize((id: string) =>
    createSelector([selectInfraredFlightById(id)], (flight) => flight?.job)
)

type UIState = {
    splitButtonOpen: boolean
    statusCheckerOpen: boolean
    selectedUpload: Record<string, boolean>
    csvFlights: Record<string, true>
}

const uiStateName = "infrared-ui-v1"
type uiStateNameType = typeof uiStateName

const {
    reducer: uiStateReducer,
    actions: { setSplitButtonOpen, setStatusCheckerOpen, setSelectedUpload, setCsvFlight },
} = createSlice({
    name: uiStateName,
    initialState: {
        splitButtonOpen: false,
        selectedUpload: {},
        statusCheckerOpen: false,
        hotspotPanelStatus: {},
        csvFlights: {},
    } as UIState,
    reducers: {
        setSplitButtonOpen: (state, action) => {
            state.splitButtonOpen = action.payload
        },
        setStatusCheckerOpen: (state, action) => {
            state.statusCheckerOpen = action.payload
        },
        setSelectedUpload: (state, action) => {
            state.selectedUpload = action.payload
        },
        setCsvFlight: (state, action) => {
            state.csvFlights[action.payload] = true
        },
    },
    extraReducers: (b) =>
        b.addCase(updateEntitySocket, (state, action) => {
            const { updates, path } = action.payload
            if (updates.error) {
                const layerName = path[0]
                if (layerName === "infraredFlights") {
                    setTimeout(() => {
                        store.dispatch(
                            setGlobalNotificationContext({
                                message: `Hotspots failed to process. Try again with a revised dataset.`,
                                status: GlobalNotificationStatus.error,
                                timeoutInMs: 7000,
                            })
                        )
                    }, 0)
                }
            }
        }),
})

const { selectSplitButtonOpen, selectStatusCheckerOpen, selectSelectedUpload, selectCsvFlights } =
    withRoot((r: Record<uiStateNameType, UIState>) => r[uiStateName], {
        selectSplitButtonOpen: () => (s) => s.splitButtonOpen,
        selectStatusCheckerOpen: () => (s) => s.statusCheckerOpen,
        selectSelectedUpload: () => (s) => s.selectedUpload,
        selectCsvFlights: () => (s) => s.csvFlights,
    })

// Parses dropped telemetry CSVs into the shared registry, flags the flight, and
// fires this slice's "csv" signal so files waiting on telemetry can proceed.
const attachCsvFiles = makeAttachCsvFiles({
    markFlightHasCsv: setCsvFlight,
    signalCsvReady: (flightId: string) => actions.signal("csv", flightId),
})

export const selectHotspotBoundingBoxForFlightId = memoize((targetFlightId) =>
    createSelector(
        [
            selectFlightOrthoBoundingBox(targetFlightId),
            selectOrthoOverlapBoundingBoxMap(),
            selectHotspotsByFlightId(targetFlightId),
            selectInfraredImagesAndUploadsByFlightId(targetFlightId),
            selectRemovedHotspotsIsVisible(targetFlightId),
        ],
        (
            orthoBoundingBox,
            orthoOverlapBoundingBoxMap,
            hotspots,
            images,
            removedHotspotsEnabled
        ) => {
            const boundingBoxPoints: GeospatialPoint[] = []

            for (const { longitude, latitude, status } of hotspots) {
                if (removedHotspotsEnabled || status !== HotspotStatus.Removed) {
                    boundingBoxPoints.push({
                        latitude,
                        longitude,
                    })
                }
            }

            if (orthoBoundingBox) {
                boundingBoxPoints.push(...orthoBoundingBox)
            }
            const orthoOverlapBoundingBox = orthoOverlapBoundingBoxMap[targetFlightId]
            if (orthoOverlapBoundingBox) {
                boundingBoxPoints.push(
                    ...orthoOverlapBoundingBox.map(
                        (corner) =>
                            ({ longitude: corner[0], latitude: corner[1] }) as GeospatialPoint
                    )
                )
            }

            for (const hotspot of hotspots) {
                if (
                    hotspot.polygon &&
                    (removedHotspotsEnabled || hotspot.status !== HotspotStatus.Removed)
                ) {
                    for (const { longitude, latitude } of hotspot.polygon) {
                        boundingBoxPoints.push({
                            latitude,
                            longitude,
                        })
                    }
                }
            }

            if (images) {
                for (const i of images) {
                    const image = isUsable(i)
                    if (image?.longitude && image?.latitude) {
                        boundingBoxPoints.push({
                            longitude: image.longitude,
                            latitude: image.latitude,
                        })
                    }
                }
            }

            if (boundingBoxPoints.length === 0) {
                return mostRecentBoundingBoxMap[targetFlightId]
            }
            const boundingBox = calculateBoundingBoxCornerPoints(boundingBoxPoints)
            mostRecentBoundingBoxMap[targetFlightId] = boundingBox
            return boundingBox
        }
    )
)

export const selectBoundingBoxForMergedFlight = memoize((flightId: ID) =>
    createSelector([selectMergedFlightById(flightId)], (flight) => {
        const state = store.getState()
        const mergedBoxes = flight.hotspotFlights.map((flight) =>
            selectHotspotBoundingBoxForFlightId(flight.id)(state)
        )
        return combineBoundingBoxes(mergedBoxes)
    })
)

export const mostRecentBoundingBoxMap: Record<ID, BoundingBox> = {}

export const selectHotspotBoundingBoxes = createSelector(
    [
        selectVisibleInfraredFlights,
        selectMergedFlights,
        selectFlightsWithRemovedHotspots,
        selectAllEntitiesMap,
    ],
    (flights, mergedFlights, _, entityMap) => {
        const state = store.getState()
        const boxes: BoundingBoxLayerData[] = []
        Object.values(flights).forEach((flight) => {
            if (flight.mergeId) return
            const box = selectHotspotBoundingBoxForFlightId(flight.id)(state)
            if (!box) return
            const polygon = box?.polygon || []
            const path = [...polygon, polygon[0]]

            boxes.push({ id: flight.id, polygon, path, area: box.area })
        })
        mergedFlights.forEach((flight) => {
            if (
                !flight.visible ||
                !!getAncestors(flight.id, entityMap).find((ancestor) => !ancestor.visible) ||
                flight.archivedTimestamp
            )
                return
            const totalBox = selectBoundingBoxForMergedFlight(flight.id)(state)
            if (!totalBox) return
            const polygon = totalBox?.polygon || []
            const path = [...polygon, polygon[0]]

            boxes.push({ id: flight.id, polygon, path, area: totalBox.area })
        })
        return boxes
    }
)

// export selectors
export {
    selectAllSets,
    selectAllUploads,
    selectSet,
    selectUpload,
    selectUploadsForSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectPendingUploadsForSet,
    selectUploadsForSetSummary,
    selectSplitButtonOpen,
    selectStatusCheckerOpen,
    selectSelectedUpload,
    selectCsvFlights,
}

// export actions
export { setSplitButtonOpen, setStatusCheckerOpen, setSelectedUpload }

// export reducer
export const reducer = {
    // ...jobsReducer,
    ...imagesReducer,
}

export const uiReducer = { [uiStateName]: uiStateReducer }

export const selectSelectedInfraredFlightId = createSelector(
    [selectLastSelectedLayerId, selectSelectedLayers],
    (lastSelectedLayerId, selectedLayers) => {
        const layer = selectedLayers.find((layer) => layer.id === lastSelectedLayerId)
        return layer?.type === SidePanelLayerType.HotspotFlight ||
            layer?.type === SidePanelLayerType.MergedFlight
            ? lastSelectedLayerId
            : undefined
    }
)
